import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useActionableApprovalsCount } from '../../hooks/useQueries';
import './BottomNav.css';

const BottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { instance, accounts, inProgress } = useMsal();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const { data: actionableApprovalsCountData } = useActionableApprovalsCount();
  const hasActionableApprovals = Number(actionableApprovalsCountData?.count || 0) > 0;
  const canSignOut = accounts && accounts.length > 0 && inProgress === 'none';

  const primaryItems = useMemo(
    () => ([
      { key: 'brainstorming', to: '/brainstorming', icon: 'bi-lightbulb', label: 'Brainstorm' },
      { key: 'items', to: '/items', icon: 'bi-clipboard-check', label: 'Items' },
      { key: 'tasks', to: '/tasks', icon: 'bi-list-task', label: 'Tasks' },
      { key: 'approvals', to: '/approvals', icon: 'bi-shield-check', label: 'Approvals' },
      { key: 'reports', to: '/reports', icon: 'bi-bar-chart', label: 'Reports' }
    ]),
    []
  );

  const overflowItems = useMemo(
    () => ([
      { key: 'trashcan', to: '/trashcan', icon: 'bi-trash', label: 'Trash' },
      { key: 'help-guide', to: '/help-guide', icon: 'bi-question-circle', label: 'Help Guide' },
      { key: 'user-settings', to: '/user-settings', icon: 'bi-person-gear', label: 'User Settings' },
      ...(canSignOut ? [{ key: 'logout', action: 'logout', icon: 'bi-box-arrow-right', label: 'Logout', danger: true }] : []),
    ]),
    [canSignOut]
  );

  const isPrimaryActive = (to) => {
    const pathname = location.pathname || '';

    // Treat route groups as active (e.g., /items and /items/:id)
    if (to === '/items') return pathname === '/items' || pathname.startsWith('/items/');
    if (to === '/tasks') return pathname === '/tasks' || pathname.startsWith('/tasks/');
    if (to === '/approvals') return pathname === '/approvals' || pathname.startsWith('/approvals/');
    if (to === '/reports') return pathname === '/reports' || pathname.startsWith('/reports/');
    if (to === '/brainstorming') return pathname === '/brainstorming' || pathname.startsWith('/brainstorming/');

    return pathname === to;
  };

  const isOverflowActive = useCallback((to) => {
    const pathname = location.pathname || '';

    if (to === '/help-guide') return pathname === '/help-guide' || pathname.startsWith('/help-guide');
    if (to === '/trashcan') return pathname === '/trashcan' || pathname.startsWith('/trashcan');
    if (to === '/user-settings') return pathname === '/user-settings' || pathname.startsWith('/user-settings');

    return pathname === to;
  }, [location.pathname]);

  const isAnyOverflowActive = useMemo(
    () => overflowItems.some((item) => isOverflowActive(item.to)),
    [overflowItems, isOverflowActive]
  );

  useEffect(() => {
    // Close overflow menu on route changes
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!menuOpen) return;

    const onPointerDown = (event) => {
      if (!menuRef.current) return;
      if (menuRef.current.contains(event.target)) return;
      setMenuOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [menuOpen]);

  const handleOverflowNavigate = (to) => {
    setMenuOpen(false);
    navigate(to);
  };

  const handleLogout = async () => {
    setMenuOpen(false);
    try {
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (error) {
      console.error('Bottom nav sign-out redirect failed:', error);
    }
  };

  return (
    <nav className="bottom-nav" aria-label="Mobile navigation">
      <div className="bottom-nav__items">
        {primaryItems.map((item) => (
          <NavLink
            key={item.key}
            to={item.to}
            className={() =>
              `bottom-nav__item ${isPrimaryActive(item.to) ? 'bottom-nav__item--active' : ''}`
            }
            aria-label={
              item.key === 'approvals' && hasActionableApprovals
                ? `${item.label} (action required)`
                : item.label
            }
          >
            <i className={`bi ${item.icon}`} aria-hidden="true" />
            {item.key === 'approvals' && hasActionableApprovals && (
              <span className="bottom-nav__badge-dot" aria-hidden="true" />
            )}
            <span className="bottom-nav__label">{item.label}</span>
          </NavLink>
        ))}

        <div className="bottom-nav__overflow" ref={menuRef}>
          <button
            type="button"
            className={`bottom-nav__item bottom-nav__overflow-btn ${menuOpen || isAnyOverflowActive ? 'bottom-nav__item--active' : ''}`}
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label="More"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          >
            <i className="bi bi-three-dots" aria-hidden="true" />
            <span className="bottom-nav__label">More</span>
          </button>

          {menuOpen && (
            <div className="bottom-nav__menu" role="menu">
              {overflowItems.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className={`bottom-nav__menu-item ${item.danger ? 'bottom-nav__menu-item--danger' : ''}`}
                  onClick={() => (item.action === 'logout' ? handleLogout() : handleOverflowNavigate(item.to))}
                  role="menuitem"
                >
                  <i className={`bi ${item.icon}`} aria-hidden="true" />
                  <span>{item.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default BottomNav;
